// ============================================================
// DashboardHomePage — Diseño SAE handoff (Indigo + Teal)
// 6 dashboards diferenciados por rol con KPIs, gráficos y cards
// ============================================================

import React, { useState, useEffect } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { AñoEscolar } from 'types/usuario.types';
import { AsignacionItem, MatriculaEstadisticas } from 'types/matricula.types';
import { EstadoCuenta } from 'types/pago.types';
import { HorarioResponse, DiaSemana } from 'types/horario.types';
import { catalogoService } from 'services/usuarioService';
import { catalogStorage } from 'utils/catalogStorage';
import { matriculaService, asignacionService } from 'services/matriculaService';
import { estadoCuentaService } from 'services/pagoService';
import { horarioService } from 'services/horarioService';
import {
  I, Card, KPI, Badge, Avatar, Donut, Bars, Spark, Progress, PageHead,
} from 'components/ui';

interface Props { onNavigate: (key: string) => void; }

// Devuelve la lista de años sin hacer llamadas API si el cache está disponible.
// Prioridad:
//   1. Lista completa en cache (llenada por precargarCatalogos después del login)
//   2. Año activo del bootstrap (cargado en index.tsx ANTES del login → siempre disponible)
//   3. Llamada API como último recurso
async function getAñosConCache(): Promise<AñoEscolar[]> {
  const cached = catalogStorage.getAñosEscolares();
  if (cached && cached.length > 0) return cached;
  const anioActivo = catalogStorage.getAnioEscolarActivo();
  if (anioActivo) return [anioActivo];
  const años = await catalogoService.getAñosEscolares();
  if (años.length > 0) catalogStorage.saveAñosEscolares(años);
  return años;
}

const DIAS: DiaSemana[] = ['DOMINGO', 'LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO'];
const HOY = DIAS[new Date().getDay()];
const HOY_LABEL = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][new Date().getDay()];

const sol = (n: number) =>
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN', minimumFractionDigits: 0 }).format(n);

const fmt12 = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
};

// ─────────────────────────────────────────────────────────────
// ADMIN Dashboard — Vista institucional consolidada
// ─────────────────────────────────────────────────────────────

const AdminDashboard: React.FC<{ onNavigate: (k: string) => void }> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [años, setAños]       = useState<AñoEscolar[]>([]);
  const [stats, setStats]     = useState<MatriculaEstadisticas | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 30_000);
    (async () => {
      setLoading(true);
      try {
        const añosList = await getAñosConCache();
        setAños(añosList);
        const añoActivo = añosList.find(a => a.activo) ?? añosList[0];
        if (añoActivo) {
          const s = await matriculaService.getEstadisticas(añoActivo.idAnioEscolar, ctrl.signal);
          setStats(s);
        }
      } catch {} finally { clearTimeout(timer); setLoading(false); }
    })();
    return () => { ctrl.abort(); clearTimeout(timer); };
  }, []);

  const año     = años.find(a => a.activo) ?? años[0];
  const inicial = stats?.porNivel.find(p => p.nivel === 'INICIAL')?.totalMatriculas    ?? 0;
  const primaria= stats?.porNivel.find(p => p.nivel === 'PRIMARIA')?.totalMatriculas   ?? 0;
  const secundaria= stats?.porNivel.find(p => p.nivel === 'SECUNDARIA')?.totalMatriculas ?? 0;
  const total        = stats?.totalMatriculas ?? 0;
  const capacidad    = stats?.capacidadTotal ?? 0;
  const capacidadPct = total > 0 && capacidad > 0 ? Math.min(100, Math.round((total / capacidad) * 100)) : 0;

  // Distribución por nivel para gráfico de barras
  const barsData = stats?.porNivel.map(p => ({ label: p.nivel.slice(0, 4), value: p.totalMatriculas })) ?? [];

  return (
    <>
      <PageHead
        title="Panel general"
        sub={`Vista consolidada · ${año ? `Año ${año.anio}` : 'Sin año activo'} · ${user?.nombres} ${user?.apellidos}`}
        actions={
          <>
            <button className="btn btn-secondary btn-hide-mobile" onClick={() => onNavigate('reportes')}>
              <I.Download /> Exportar
            </button>
            <button className="btn btn-primary" onClick={() => onNavigate('usuarios')}>
              <I.Plus /> Nuevo usuario
            </button>
          </>
        }
      />

      {/* KPIs */}
      <div className="kpi-grid mb-4">
        <KPI
          label="Matriculados"
          value={loading ? '…' : total.toLocaleString()}
          trend={loading ? '' : `${stats?.porNivel.length ?? 0} niveles activos`}
          trendDir="up"
          icon="Users"
          color="indigo"
        />
        <KPI
          label="Años escolares"
          value={loading ? '…' : String(años.length)}
          trend={año ? `${año.anio} activo` : '—'}
          icon="Calendar"
          color="teal"
        />
        <KPI
          label="Pagos pendientes"
          value={loading ? '…' : String(stats?.pagosMatriculaPendientesCount ?? 0)}
          trend={stats ? sol(stats.pagosMatriculaPendientesMonto) : ''}
          trendDir="down"
          icon="AlertCircle"
          color="amber"
        />
        <KPI
          label="Por cobrar"
          value={loading ? '…' : sol(stats?.totalACobrar ?? 0)}
          trend="Pensiones + matrículas"
          icon="DollarSign"
          color="green"
        />
      </div>

      {/* Distribución y matrículas por nivel */}
      <div className="grid-12 mb-4">
        <Card title="Matrículas por nivel" sub={año ? `Año ${año.anio}` : ''}>
          {barsData.length > 0
            ? (
              <>
                <Bars data={barsData} />
                <div className="flex justify-between mt-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
                  <div>
                    <div className="muted text-[11px]">Total</div>
                    <b className="text-[17px]">{total.toLocaleString()} matriculados</b>
                  </div>
                  <div>
                    <div className="muted text-[11px]">Niveles</div>
                    <b className="text-[17px]">{stats?.porNivel.length ?? 0}</b>
                  </div>
                  <div>
                    <div className="muted text-[11px]">Capacidad</div>
                    <b className="text-[17px]" style={{ color: 'var(--success)' }}>{capacidadPct}%</b>
                  </div>
                </div>
              </>
            )
            : (
              <div className="text-center py-8 muted text-sm">Sin datos de matrícula</div>
            )
          }
        </Card>

        <Card title="Distribución por nivel">
          {capacidad > 0
            ? (
              <div className="flex justify-center py-2">
                <Donut value={capacidadPct} label="Capacidad" />
              </div>
            )
            : (
              <div className="text-center py-4 text-xs muted">
                Sin capacidad configurada —{' '}
                <span className="text-primary-600 font-semibold">edita las secciones</span>
              </div>
            )
          }
          <div className="grid gap-2 mt-3">
            {[
              { l: 'Inicial',    v: inicial,    c: '#f59e0b' },
              { l: 'Primaria',   v: primaria,   c: '#3b82f6' },
              { l: 'Secundaria', v: secundaria, c: '#8b5cf6' },
            ].map((n, i) => (
              <div key={i} className="row between">
                <div className="row">
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: n.c }} />
                  <span className="text-[13px]">{n.l}</span>
                </div>
                <b className="text-[13px] tabular">{n.v}</b>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Acciones rápidas y configuración */}
      <div className="grid-2">
        <Card title="Acciones rápidas">
          <div className="grid grid-cols-2 gap-3">
            {[
              { k: 'usuarios',          l: 'Usuarios',     i: 'Users',         c: 'indigo' },
              { k: 'matriculas',        l: 'Matrículas',   i: 'CalendarCheck', c: 'teal'   },
              { k: 'cobros',            l: 'Cobros',       i: 'DollarSign',    c: 'amber'  },
              { k: 'config-pagos',      l: 'Tarifas',      i: 'Wallet',        c: 'rose'   },
              { k: 'reportes',          l: 'Reportes',     i: 'BarChart',      c: 'green'  },
              { k: 'configuracion',     l: 'Configuración',i: 'Settings',      c: 'blue'   },
            ].map(qa => {
              const Icon = I[qa.i as keyof typeof I];
              const c = qa.c === 'indigo' ? { bg: 'var(--primary-100)', fg: 'var(--primary-700)' }
                     : qa.c === 'teal'   ? { bg: 'var(--accent-100)',  fg: 'var(--accent-700)' }
                     : qa.c === 'amber'  ? { bg: '#fef3c7', fg: '#92400e' }
                     : qa.c === 'rose'   ? { bg: '#fee2e2', fg: '#991b1b' }
                     : qa.c === 'green'  ? { bg: '#dcfce7', fg: '#166534' }
                     :                     { bg: '#dbeafe', fg: '#1e40af' };
              return (
                <button
                  key={qa.k}
                  onClick={() => onNavigate(qa.k)}
                  className="row p-3 rounded-r border bg-white hover:shadow-soft transition-all text-left"
                  style={{ gap: 12, borderColor: 'var(--border)' }}
                >
                  <div
                    className="rounded-r-sm grid place-items-center flex-shrink-0"
                    style={{ width: 36, height: 36, background: c.bg, color: c.fg }}
                  >
                    <Icon size={18} />
                  </div>
                  <span className="text-[13px] font-semibold text-ink-900">{qa.l}</span>
                </button>
              );
            })}
          </div>
        </Card>

        <Card title="Estado del sistema" action={<Badge kind="success" dot>Operativo</Badge>}>
          <div className="grid gap-2.5 text-[13px]">
            <div className="row between">
              <span className="muted">Año escolar activo</span>
              <Badge kind="indigo">{año?.anio ?? '—'}</Badge>
            </div>
            <div className="row between">
              <span className="muted">Fase</span>
              <b>{año?.fase ?? '—'}</b>
            </div>
            <div className="row between">
              <span className="muted">Total estudiantes</span>
              <b>{total.toLocaleString()}</b>
            </div>
            <div className="row between">
              <span className="muted">Versión sistema</span>
              <b className="font-mono text-[12px]">SAE 1.0.0</b>
            </div>
            <div className="divider" />
            <button className="btn btn-secondary w-full justify-center" onClick={() => onNavigate('config-institucional')}>
              <I.Settings size={14} /> Configuración institucional
            </button>
          </div>
        </Card>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// DIRECTOR Dashboard — Vista ejecutiva
// ─────────────────────────────────────────────────────────────

const DirectorDashboard: React.FC<{ onNavigate: (k: string) => void }> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [años, setAños]           = useState<AñoEscolar[]>([]);
  const [stats, setStats]         = useState<MatriculaEstadisticas | null>(null);
  const [grados, setGrados]       = useState<number>(0);
  const [secciones, setSecciones] = useState<number>(0);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 30_000);
    (async () => {
      setLoading(true);
      try {
        const añosList = await getAñosConCache();
        const grds = catalogStorage.getGrados() ?? await catalogoService.getGrados();
        const secs = catalogStorage.getSecciones() ?? await catalogoService.getSecciones();
        setAños(añosList);
        setGrados(grds.length);
        setSecciones(secs.length);
        const añoActivo = añosList.find(a => a.activo) ?? añosList[0];
        if (añoActivo) {
          const s = await matriculaService.getEstadisticas(añoActivo.idAnioEscolar, ctrl.signal);
          setStats(s);
        }
      } catch {} finally { clearTimeout(timer); setLoading(false); }
    })();
    return () => { ctrl.abort(); clearTimeout(timer); };
  }, []);

  const año          = años.find(a => a.activo) ?? años[0];
  const total        = stats?.totalMatriculas ?? 0;
  const capacidad    = stats?.capacidadTotal ?? 0;
  const capacidadPct = total > 0 && capacidad > 0 ? Math.min(100, Math.round((total / capacidad) * 100)) : 0;
  const barsData = stats?.porNivel.map(p => ({ label: p.nivel.slice(0, 4), value: p.totalMatriculas })) ?? [];

  return (
    <>
      <PageHead
        title="Panel de Dirección"
        sub={`Hola ${user?.nombres} 👋 · ${año ? `Año académico ${año.anio}` : 'Sin año activo'}`}
        actions={
          <button className="btn btn-primary" onClick={() => onNavigate('reportes')}>
            <I.BarChart /> Ver reportes
          </button>
        }
      />

      <div className="kpi-grid mb-4">
        <KPI label="Estudiantes" value={loading ? '…' : total.toLocaleString()} trend={`${stats?.porNivel.length ?? 0} niveles`} icon="Users" color="indigo" />
        <KPI label="Docentes activos" value={loading ? '…' : String(grados)} trend="Asignaciones vigentes" icon="Briefcase" color="teal" />
        <KPI label="Grados activos" value={loading ? '…' : String(grados)} icon="BookOpen" color="blue" />
        <KPI label="Secciones" value={loading ? '…' : String(secciones)} trend={`${grados} grados`} icon="Layers" color="amber" />
      </div>

      <div className="grid-12 mb-4">
        <Card title="Distribución por nivel" sub={año ? `Año ${año.anio}` : ''}>
          {barsData.length > 0
            ? <Bars data={barsData} />
            : <div className="text-center py-8 muted text-sm">Sin datos</div>
          }
        </Card>

        <Card title="Capacidad institucional">
          {capacidad > 0
            ? (
              <>
                <div className="flex justify-center py-2">
                  <Donut value={capacidadPct} label="ocupación" />
                </div>
                <p className="text-center text-xs muted mt-2">
                  {total} de {capacidad} cupos disponibles
                </p>
              </>
            )
            : (
              <div className="text-center py-6 text-xs muted">
                Sin capacidad configurada —{' '}
                <span className="text-primary-600 font-semibold">edita las secciones</span>
              </div>
            )
          }
        </Card>
      </div>

      <div className="grid-2">
        <Card title="Acciones de dirección">
          <div className="grid gap-2.5">
            {[
              { k: 'promocion',     l: 'Promoción / Cierre',     i: 'Award'    },
              { k: 'orden-merito',  l: 'Orden de mérito',        i: 'Star'     },
              { k: 'traslados',     l: 'Traslados',              i: 'Send'     },
              { k: 'nominas-matricula', l: 'Nóminas de matrícula', i: 'FileText' },
            ].map(a => {
              const Icon = I[a.i as keyof typeof I];
              return (
                <button
                  key={a.k}
                  onClick={() => onNavigate(a.k)}
                  className="row p-3 rounded-r border bg-white hover:bg-surface-alt transition-all text-left"
                  style={{ gap: 12, borderColor: 'var(--border)' }}
                >
                  <div
                    className="rounded-r-sm grid place-items-center flex-shrink-0"
                    style={{ width: 36, height: 36, background: 'var(--primary-100)', color: 'var(--primary-700)' }}
                  >
                    <Icon size={18} />
                  </div>
                  <span className="text-[13px] font-semibold flex-1">{a.l}</span>
                  <I.ChevronRight size={14} className="text-ink-400" />
                </button>
              );
            })}
          </div>
        </Card>

        <Card title="Resumen institucional">
          <div className="grid gap-2.5 text-[13px]">
            <div className="row between"><span className="muted">Año vigente</span><Badge kind="success">{año?.anio ?? '—'}</Badge></div>
            <div className="row between"><span className="muted">Fase</span><b>{año?.fase ?? '—'}</b></div>
            <div className="row between"><span className="muted">Total matriculados</span><b className="text-primary-700">{total.toLocaleString()}</b></div>
            <div className="row between"><span className="muted">Grados</span><b>{grados}</b></div>
            <div className="row between"><span className="muted">Secciones</span><b>{secciones}</b></div>
            <div className="divider" />
            <button className="btn btn-secondary w-full justify-center" onClick={() => onNavigate('reportes')}>
              <I.Eye size={14} /> Ver reportes detallados
            </button>
          </div>
        </Card>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// SECRETARIA Dashboard — Vista operativa
// ─────────────────────────────────────────────────────────────

const SecretariaDashboard: React.FC<{ onNavigate: (k: string) => void }> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [años, setAños]       = useState<AñoEscolar[]>([]);
  const [stats, setStats]     = useState<MatriculaEstadisticas | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 30_000);
    (async () => {
      setLoading(true);
      try {
        const añosList = await getAñosConCache();
        setAños(añosList);
        const añoActivo = añosList.find(a => a.activo) ?? añosList[0];
        if (añoActivo) {
          const s = await matriculaService.getEstadisticas(añoActivo.idAnioEscolar, ctrl.signal);
          setStats(s);
        }
      } catch {} finally { clearTimeout(timer); setLoading(false); }
    })();
    return () => { ctrl.abort(); clearTimeout(timer); };
  }, []);

  const año = años.find(a => a.activo) ?? años[0];
  const total = stats?.totalMatriculas ?? 0;
  const pendCobros = (stats?.pagosMatriculaPendientesCount ?? 0) + (stats?.pensionesPendientesCount ?? 0);

  return (
    <>
      <PageHead
        title="Panel de Secretaría"
        sub={`Hola ${user?.nombres} 👋 · ${año ? `Gestión ${año.anio}` : ''}`}
        actions={
          <button className="btn btn-primary" onClick={() => onNavigate('cobros')}>
            <I.DollarSign /> Registrar cobro
          </button>
        }
      />

      <div className="kpi-grid mb-4">
        <KPI label="Matriculados" value={loading ? '…' : total.toLocaleString()} icon="Users" color="indigo" />
        <KPI
          label="Pagos matrícula pend."
          value={loading ? '…' : String(stats?.pagosMatriculaPendientesCount ?? 0)}
          trend={stats ? sol(stats.pagosMatriculaPendientesMonto) : ''}
          trendDir="down"
          icon="AlertCircle"
          color="amber"
        />
        <KPI
          label="Pensiones pendientes"
          value={loading ? '…' : String(stats?.pensionesPendientesCount ?? 0)}
          trend={stats ? sol(stats.pensionesPendientesMonto) : ''}
          trendDir="down"
          icon="Clock"
          color="rose"
        />
        <KPI
          label="Total por cobrar"
          value={loading ? '…' : sol(stats?.totalACobrar ?? 0)}
          trend={`${pendCobros} cuotas`}
          icon="DollarSign"
          color="green"
        />
      </div>

      {!loading && stats && pendCobros > 0 && (
        <Card title="Desglose de cobros pendientes" className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div
              className="row p-4 rounded-r border"
              style={{
                background: 'var(--warning-bg)',
                borderColor: '#fcd34d',
                gap: 14,
              }}
            >
              <div
                className="rounded-r-sm grid place-items-center flex-shrink-0"
                style={{ width: 44, height: 44, background: '#fef3c7', color: '#92400e' }}
              >
                <I.CalendarCheck size={22} />
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#92400e' }}>
                  Matrículas
                </p>
                <p className="text-[22px] font-extrabold tabular m-0" style={{ color: '#78350f' }}>
                  {sol(stats.pagosMatriculaPendientesMonto)}
                </p>
                <p className="text-xs" style={{ color: '#92400e' }}>
                  {stats.pagosMatriculaPendientesCount} pago{stats.pagosMatriculaPendientesCount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div
              className="row p-4 rounded-r border"
              style={{
                background: 'var(--danger-bg)',
                borderColor: '#fca5a5',
                gap: 14,
              }}
            >
              <div
                className="rounded-r-sm grid place-items-center flex-shrink-0"
                style={{ width: 44, height: 44, background: '#fee2e2', color: '#991b1b' }}
              >
                <I.Clock size={22} />
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: '#991b1b' }}>
                  Pensiones
                </p>
                <p className="text-[22px] font-extrabold tabular m-0" style={{ color: '#7f1d1d' }}>
                  {sol(stats.pensionesPendientesMonto)}
                </p>
                <p className="text-xs" style={{ color: '#991b1b' }}>
                  {stats.pensionesPendientesCount} cuota{stats.pensionesPendientesCount !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card title="Acciones rápidas">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { k: 'cobros',       l: 'Cobros',         i: 'DollarSign', c: { bg: '#dcfce7', fg: '#166534' } },
            { k: 'matriculas',   l: 'Matrículas',     i: 'CalendarCheck', c: { bg: 'var(--primary-100)', fg: 'var(--primary-700)' } },
            { k: 'estudiantes',  l: 'Estudiantes',    i: 'Users',      c: { bg: 'var(--accent-100)', fg: 'var(--accent-700)' } },
            { k: 'reportes',     l: 'Reportes',       i: 'BarChart',   c: { bg: '#dbeafe', fg: '#1e40af' } },
          ].map(qa => {
            const Icon = I[qa.i as keyof typeof I];
            return (
              <button
                key={qa.k}
                onClick={() => onNavigate(qa.k)}
                className="flex flex-col items-center gap-2 p-4 rounded-r border bg-white hover:shadow-soft transition-all"
                style={{ borderColor: 'var(--border)' }}
              >
                <div
                  className="rounded-r-sm grid place-items-center"
                  style={{ width: 40, height: 40, background: qa.c.bg, color: qa.c.fg }}
                >
                  <Icon size={20} />
                </div>
                <span className="text-[12.5px] font-semibold text-ink-900">{qa.l}</span>
              </button>
            );
          })}
        </div>
      </Card>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// DOCENTE Dashboard — Mis clases
// ─────────────────────────────────────────────────────────────

const DocenteDashboard: React.FC<{ onNavigate: (k: string) => void }> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [asignaciones, setAsignaciones] = useState<AsignacionItem[]>([]);
  const [horarioHoy, setHorarioHoy]     = useState<HorarioResponse[]>([]);
  const [anioActual, setAnioActual]     = useState<AñoEscolar | null>(null);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    if (!user?.idUsuario) return;
    (async () => {
      setLoading(true);
      try {
        const [años, asig] = await Promise.all([
          catalogoService.getAñosEscolares(),
          asignacionService.getByDocente(user.idUsuario),
        ]);
        const año = años[0] ?? null;
        setAnioActual(año);

        const asigActuales = año ? asig.filter(a => a.anio === año.anio) : asig;
        setAsignaciones(asigActuales);

        if (año) {
          const horario = await horarioService.listarPorDocente(user.idUsuario, año.idAnioEscolar);
          setHorarioHoy(
            horario
              .filter(h => h.dia === HOY && h.activo)
              .sort((a, b) => a.horaInicio.localeCompare(b.horaInicio)),
          );
        }
      } catch {} finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.idUsuario]);

  const cursos = new Set(asignaciones.map(a => a.cursoId)).size;
  const secciones = new Set(asignaciones.map(a => `${a.gradoId}-${a.seccionId}`)).size;

  return (
    <>
      <PageHead
        title={`Hola ${user?.nombres} 👋`}
        sub={
          loading
            ? 'Cargando datos…'
            : `Tienes ${horarioHoy.length} clase${horarioHoy.length !== 1 ? 's' : ''} hoy${
                anioActual ? ` · Año ${anioActual.anio}` : ''
              }`
        }
      />

      <div className="kpi-grid mb-4">
        <KPI label="Cursos asignados" value={loading ? '…' : String(cursos)} trend={`${asignaciones.length} asignaciones`} icon="BookOpen" color="indigo" />
        <KPI label="Secciones" value={loading ? '…' : String(secciones)} icon="Layers" color="teal" />
        <KPI label="Clases hoy" value={loading ? '…' : String(horarioHoy.length)} trend={HOY_LABEL} icon="Clock" color="green" />
        <KPI label="Total asignaciones" value={loading ? '…' : String(asignaciones.length)} icon="Calendar" color="amber" />
      </div>

      <div className="grid-12">
        <Card
          title="Mi horario de hoy"
          sub={HOY_LABEL}
          action={
            <button className="btn btn-ghost btn-sm" onClick={() => onNavigate('horarios')}>
              Ver semana <I.ChevronRight size={14} />
            </button>
          }
        >
          {horarioHoy.length === 0 ? (
            <div className="text-center py-8">
              <I.CheckCircle size={32} className="mx-auto mb-2 text-ink-300" />
              <p className="text-sm muted m-0">Sin clases programadas para hoy</p>
              <p className="text-xs muted mt-1">¡Aprovecha el descanso!</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {horarioHoy.map(h => (
                <div
                  key={h.idHorario}
                  className="row p-3 rounded-r"
                  style={{ background: 'var(--surface-2)', gap: 12 }}
                >
                  <div style={{ width: 4, height: 36, background: 'var(--primary-500)', borderRadius: 2 }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-ink-900">
                      {h.cursoNombre}
                      <span className="muted font-normal ml-1.5 text-[12px]">· {h.gradoNombre} {h.seccionNombre}</span>
                    </div>
                    <div className="text-[11px] muted">
                      {fmt12(h.horaInicio)} — {fmt12(h.horaFin)}
                    </div>
                  </div>
                  <button
                    className="btn btn-secondary btn-sm flex-shrink-0"
                    onClick={() => onNavigate('asistencia')}
                  >
                    <I.CheckCircle size={12} /> Asistencia
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title="Mis asignaciones" action={<Badge kind="indigo">{asignaciones.length}</Badge>}>
          {loading ? (
            <div className="text-center py-6 muted text-sm">Cargando…</div>
          ) : asignaciones.length === 0 ? (
            <div className="text-center py-6 muted text-sm">Sin asignaciones</div>
          ) : (
            <div className="grid gap-2">
              {asignaciones.slice(0, 5).map(a => (
                <div
                  key={a.idAsignacion}
                  className="row between p-2.5 rounded-r-sm border"
                  style={{ borderColor: 'var(--border)' }}
                >
                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-ink-900 m-0 truncate">{a.cursoNombre}</p>
                    <p className="text-[11px] muted m-0 truncate">
                      {a.gradoNombre} · Sección {a.seccionNombre}
                    </p>
                  </div>
                  <Badge
                    kind={
                      a.gradoNivel === 'INICIAL' ? 'warning'
                      : a.gradoNivel === 'PRIMARIA' ? 'info'
                      : 'indigo'
                    }
                  >
                    {a.gradoNivel}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// ESTUDIANTE Dashboard — Mis estudios
// ─────────────────────────────────────────────────────────────

const EstudianteDashboard: React.FC<{ onNavigate: (k: string) => void }> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [cuenta, setCuenta]         = useState<EstadoCuenta | null>(null);
  const [horarioHoy, setHorarioHoy] = useState<HorarioResponse[]>([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    if (!user?.idUsuario) return;
    (async () => {
      setLoading(true);
      try {
        const años = await catalogoService.getAñosEscolares();
        const año = años[0] ?? null;
        if (año) {
          const [cta, horario] = await Promise.all([
            estadoCuentaService.getByEstudiante(user.idUsuario, año.idAnioEscolar),
            horarioService.listarPorEstudiante(user.idUsuario, año.idAnioEscolar),
          ]);
          setCuenta(cta);
          setHorarioHoy(
            horario.filter(h => h.dia === HOY && h.activo)
              .sort((a, b) => a.horaInicio.localeCompare(b.horaInicio)),
          );
        }
      } catch {} finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.idUsuario]);

  const resumen = cuenta?.resumen;
  const pctPagado = resumen && (resumen.cuotasPagadas + resumen.cuotasPendientes) > 0
    ? Math.round((resumen.cuotasPagadas / (resumen.cuotasPagadas + resumen.cuotasPendientes)) * 100)
    : 0;

  return (
    <>
      <PageHead
        title={`Hola ${user?.nombres} 👋`}
        sub={cuenta ? `${cuenta.gradoNombre} · Sección ${cuenta.seccionNombre} · Año ${cuenta.anio}` : 'Mi panel estudiantil'}
      />

      {/* Banner estudiante con avatar */}
      <Card pad={false} className="mb-4">
        <div
          className="p-6 flex items-center gap-5 flex-wrap rounded-t-r-lg"
          style={{ background: 'linear-gradient(135deg, var(--primary-50), var(--accent-50))' }}
        >
          <Avatar name={`${user?.nombres ?? ''} ${user?.apellidos ?? ''}`} size="lg" />
          <div className="flex-1 min-w-0" style={{ minWidth: 200 }}>
            <div className="text-[18px] font-extrabold text-ink-900">
              {user?.nombres} {user?.apellidos}
            </div>
            <div className="text-[13px] muted">
              {cuenta
                ? `${cuenta.nivel} · ${cuenta.gradoNombre} · Sección ${cuenta.seccionNombre}`
                : 'Estudiante'
              }
              {user?.dni && <> · DNI {user.dni}</>}
            </div>
          </div>
          {resumen && (
            <div className="flex gap-6">
              <div>
                <div className="muted text-[11px]">Cuotas pagadas</div>
                <b className="text-[22px]" style={{ color: 'var(--primary-700)' }}>
                  {resumen.cuotasPagadas}/{resumen.cuotasPagadas + resumen.cuotasPendientes}
                </b>
              </div>
              <div>
                <div className="muted text-[11px]">% pagado</div>
                <b className="text-[22px]" style={{ color: 'var(--success)' }}>{pctPagado}%</b>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* KPIs */}
      <div className="kpi-grid mb-4">
        <KPI label="Cuotas pagadas" value={loading ? '…' : String(resumen?.cuotasPagadas ?? 0)} icon="CheckCircle" color="green" />
        <KPI label="Cuotas pendientes" value={loading ? '…' : String(resumen?.cuotasPendientes ?? 0)} icon="Clock" color="amber" />
        <KPI label="Por pagar" value={loading ? '…' : (resumen ? sol(resumen.totalPendiente) : 'S/ 0')} icon="DollarSign" color="rose" />
        <KPI label="Clases hoy" value={loading ? '…' : String(horarioHoy.length)} trend={HOY_LABEL} icon="Calendar" color="indigo" />
      </div>

      {/* Estado de cuenta y horario */}
      <div className="grid-12 mb-4">
        {resumen && (resumen.cuotasPagadas + resumen.cuotasPendientes) > 0 && (
          <Card title="Estado de cuenta" sub="Resumen anual">
            <div className="grid gap-3">
              <div>
                <div className="row between mb-1.5">
                  <span className="text-[13px] muted">
                    {resumen.cuotasPagadas} de {resumen.cuotasPagadas + resumen.cuotasPendientes} cuotas pagadas
                  </span>
                  <b className="text-[13px] text-ink-900">{pctPagado}%</b>
                </div>
                <Progress value={pctPagado} />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div className="text-center p-3 rounded-r" style={{ background: '#dcfce7' }}>
                  <p className="text-[18px] font-extrabold tabular m-0" style={{ color: '#166534' }}>
                    {sol(resumen.totalPagado)}
                  </p>
                  <p className="text-xs m-0" style={{ color: '#15803d' }}>Pagado</p>
                </div>
                <div className="text-center p-3 rounded-r" style={{ background: '#fef3c7' }}>
                  <p className="text-[18px] font-extrabold tabular m-0" style={{ color: '#92400e' }}>
                    {sol(resumen.totalPendiente)}
                  </p>
                  <p className="text-xs m-0" style={{ color: '#b45309' }}>Pendiente</p>
                </div>
                <div className="text-center p-3 rounded-r bg-surface-alt">
                  <p className="text-[18px] font-extrabold tabular m-0 text-ink-900">
                    {sol(resumen.totalCargado)}
                  </p>
                  <p className="text-xs muted m-0">Total</p>
                </div>
              </div>
              <button className="btn btn-primary w-full justify-center mt-1" onClick={() => onNavigate('pagos')}>
                <I.Wallet size={14} /> Ver mis pagos
              </button>
            </div>
          </Card>
        )}

        <Card title={`Mi horario · ${HOY_LABEL}`}>
          {horarioHoy.length === 0 ? (
            <div className="text-center py-6">
              <I.CheckCircle size={28} className="mx-auto mb-2 text-ink-300" />
              <p className="text-sm muted m-0">Sin clases hoy</p>
            </div>
          ) : (
            <div className="grid gap-2">
              {horarioHoy.slice(0, 5).map(h => (
                <div
                  key={h.idHorario}
                  className="row p-2.5 rounded-r-sm border"
                  style={{ borderColor: 'var(--border)', gap: 10 }}
                >
                  <div className="text-[11px] tabular muted" style={{ width: 90 }}>
                    {fmt12(h.horaInicio)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold text-ink-900 m-0 truncate">{h.cursoNombre}</p>
                    <p className="text-[11px] muted m-0 truncate">{h.docenteApellidos}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Acciones rápidas */}
      <Card title="Acciones rápidas">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { k: 'notas',     l: 'Mis Notas',    i: 'Award',    c: { bg: 'var(--primary-100)', fg: 'var(--primary-700)' } },
            { k: 'asistencia',l: 'Asistencia',   i: 'CheckCircle', c: { bg: '#dcfce7', fg: '#166534' } },
            { k: 'horario',   l: 'Mi Horario',   i: 'Calendar', c: { bg: 'var(--accent-100)', fg: 'var(--accent-700)' } },
            { k: 'pagos',     l: 'Mis Pagos',    i: 'Wallet',   c: { bg: '#fef3c7', fg: '#92400e' } },
          ].map(qa => {
            const Icon = I[qa.i as keyof typeof I];
            return (
              <button
                key={qa.k}
                onClick={() => onNavigate(qa.k)}
                className="flex flex-col items-center gap-2 p-4 rounded-r border bg-white hover:shadow-soft transition-all"
                style={{ borderColor: 'var(--border)' }}
              >
                <div
                  className="rounded-r-sm grid place-items-center"
                  style={{ width: 40, height: 40, background: qa.c.bg, color: qa.c.fg }}
                >
                  <Icon size={20} />
                </div>
                <span className="text-[12.5px] font-semibold text-ink-900">{qa.l}</span>
              </button>
            );
          })}
        </div>
      </Card>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// PADRE Dashboard — Apoderado
// ─────────────────────────────────────────────────────────────

const PadreDashboard: React.FC<{ onNavigate: (k: string) => void }> = ({ onNavigate }) => {
  const { user } = useAuth();

  return (
    <>
      <PageHead
        title={`Bienvenido ${user?.nombres ?? ''}`}
        sub="Resumen escolar de tu hijo/a"
      />

      <Card pad={false} className="mb-4">
        <div
          className="p-6 flex items-center gap-5 flex-wrap"
          style={{
            background: 'linear-gradient(135deg, var(--primary-50), var(--accent-50))',
            borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
          }}
        >
          <Avatar name={`${user?.nombres ?? ''} ${user?.apellidos ?? ''}`} size="lg" />
          <div className="flex-1 min-w-0" style={{ minWidth: 200 }}>
            <div className="text-[18px] font-extrabold text-ink-900">
              {user?.nombres} {user?.apellidos}
            </div>
            <div className="text-[13px] muted">
              Apoderado{user?.dni && <> · DNI {user.dni}</>}
            </div>
          </div>
        </div>
      </Card>

      <div className="grid-2">
        <Card title="Acceso a información de hijos/as">
          <div className="grid gap-2.5">
            {[
              { k: 'hijos', l: 'Mis hijos', i: 'Users' },
            ].map(a => {
              const Icon = I[a.i as keyof typeof I];
              return (
                <button
                  key={a.k}
                  onClick={() => onNavigate(a.k)}
                  className="row p-3 rounded-r border bg-white hover:bg-surface-alt transition-all text-left"
                  style={{ gap: 12, borderColor: 'var(--border)' }}
                >
                  <div
                    className="rounded-r-sm grid place-items-center flex-shrink-0"
                    style={{ width: 36, height: 36, background: 'var(--primary-100)', color: 'var(--primary-700)' }}
                  >
                    <Icon size={18} />
                  </div>
                  <span className="text-[13px] font-semibold flex-1">{a.l}</span>
                  <I.ChevronRight size={14} className="text-ink-400" />
                </button>
              );
            })}
          </div>
        </Card>

        <Card title="Información de contacto" action={<Badge kind="info">Soporte</Badge>}>
          <div className="grid gap-3">
            <div className="row p-3 rounded-r" style={{ background: 'var(--surface-2)', gap: 12 }}>
              <div
                className="rounded-r-sm grid place-items-center flex-shrink-0"
                style={{ width: 36, height: 36, background: 'var(--primary-100)', color: 'var(--primary-700)' }}
              >
                <I.School size={18} />
              </div>
              <div>
                <p className="text-[11px] muted m-0 uppercase font-bold tracking-wide">Secretaría</p>
                <p className="text-[13px] font-semibold text-ink-900 m-0">
                  Para consultas académicas y de pagos
                </p>
              </div>
            </div>
            <div className="row p-3 rounded-r" style={{ background: 'var(--surface-2)', gap: 12 }}>
              <div
                className="rounded-r-sm grid place-items-center flex-shrink-0"
                style={{ width: 36, height: 36, background: 'var(--accent-100)', color: 'var(--accent-700)' }}
              >
                <I.User size={18} />
              </div>
              <div>
                <p className="text-[11px] muted m-0 uppercase font-bold tracking-wide">Dirección</p>
                <p className="text-[13px] font-semibold text-ink-900 m-0">
                  Asuntos académicos y disciplinarios
                </p>
              </div>
            </div>
            <div className="row p-3 rounded-r" style={{ background: 'var(--surface-2)', gap: 12 }}>
              <div
                className="rounded-r-sm grid place-items-center flex-shrink-0"
                style={{ width: 36, height: 36, background: '#fef3c7', color: '#92400e' }}
              >
                <I.Bell size={18} />
              </div>
              <div>
                <p className="text-[11px] muted m-0 uppercase font-bold tracking-wide">Notificaciones</p>
                <p className="text-[13px] font-semibold text-ink-900 m-0">
                  Comunicados y alertas institucionales
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────

const DashboardHomePage: React.FC<Props> = ({ onNavigate }) => {
  const { user } = useAuth();
  const rol = user?.rol;

  switch (rol) {
    case 'ADMIN':      return <AdminDashboard      onNavigate={onNavigate} />;
    case 'DIRECTOR':   return <DirectorDashboard   onNavigate={onNavigate} />;
    case 'SECRETARIA': return <SecretariaDashboard onNavigate={onNavigate} />;
    case 'DOCENTE':    return <DocenteDashboard    onNavigate={onNavigate} />;
    case 'ESTUDIANTE': return <EstudianteDashboard onNavigate={onNavigate} />;
    case 'PADRE':      return <PadreDashboard      onNavigate={onNavigate} />;
    default:           return null;
  }
};

export default DashboardHomePage;
